import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { STATES_LIST } from '@umss/shared/data-access';
import { SelectOption, UmssSelectComponent } from '../select/select.component';

@Component({
  selector: 'umss-state-select',
  standalone: true,
  imports: [CommonModule, UmssSelectComponent],
  template: `
    <umss-select
      [label]="label"
      [placeholder]="placeholder"
      [options]="options"
      [value]="value"
      [searchable]="searchable"
      [clearable]="clearable"
      [multiple]="multiple"
      [disabled]="disabled"
      [required]="required"
      [size]="size"
      (valueChange)="onValueChange($event)"
      (searchChange)="onSearchChange($event)"
    />
  `,
  styles: `
    :host {
      display: contents;
    }

    ::ng-deep .vertical-field {
      flex: 1;
      min-width: 0;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssStateSelectComponent),
      multi: true
    }
  ]
})
export class UmssStateSelectComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = 'Select state';
  @Input() searchable = true;
  @Input() clearable = true;
  @Input() multiple = false;
  @Input() disabled = false;
  @Input() label: string | undefined = undefined;
  @Input() required = false;
  @Input() showAllOption = false;
  @Input() allOptionLabel = 'All';
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';

  options: SelectOption[] = [];
  value: string | string[] | null = null;

  private readonly ALL_OPTION: SelectOption = { value: 'all', label: 'All' };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: string | string[] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  ngOnInit(): void {
    this.options = this.buildOptions(STATES_LIST);
  }

  writeValue(value: string | string[] | null): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string | string[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  onValueChange(value: string | string[] | null): void {
    if (this.showAllOption && this.multiple && Array.isArray(value)) {
      const allValue = this.ALL_OPTION.value;
      const previous = Array.isArray(this.value)
        ? (this.value as string[])
        : [];
      const hadAll = previous.includes(allValue);
      const hasAll = value.includes(allValue);

      if (!hadAll && hasAll) {
        // User selected "All" → clear every other selection
        value = [allValue];
      } else if (hadAll && hasAll) {
        // User selected a normal option while "All" was active → drop "All"
        value = value.filter((v) => v !== allValue);
      }
    }

    this.value = value;
    this.onChange(value);
    this.onTouched();
  }

  onSearchChange(searchTerm: string | null): void {
    if (!searchTerm) {
      this.options = this.buildOptions(STATES_LIST);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = STATES_LIST.filter(
      (state) =>
        state.label.toLowerCase().includes(term) ||
        state.value.toLowerCase().includes(term)
    );
    this.options = this.buildOptions(filtered);
  }

  private buildOptions(states: SelectOption[]): SelectOption[] {
    if (this.showAllOption) {
      return [{ ...this.ALL_OPTION, label: this.allOptionLabel }, ...states];
    }
    return states;
  }
}
